import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, Layers3, Zap, ImageIcon, AlertTriangle } from "lucide-react";
import Atelier from "@/pages/Atelier";
import Generator from "@/pages/Generator";
import { RecoveredVisuals } from "@/components/RecoveredVisuals";

/**
 * Porte d'entrée unique de la création de posts.
 * Enveloppe les deux pages existantes sans toucher à leur logique :
 *  - « Atelier » (par défaut) : on dépose, on ferme Pulse, on récupère plus tard.
 *  - « Mode rapide » : rédaction immédiate, avec affinage et personnalisation.
 * Les routes /atelier et /generer restent valides et inchangées.
 */
export default function Creer() {
  const location = useLocation();
  // Un angle envoyé depuis la veille, ou un prompt réutilisé depuis « Mes posts »,
  // ouvre directement le mode rapide.
  const [tab, setTab] = useState(() => {
    if (location.state?.prefill) return "rapide";
    try { if (sessionStorage.getItem("reuse_prompt")) return "rapide"; } catch (e) { /* stockage indisponible */ }
    return "atelier";
  });
  const [ready, setReady] = useState(0);
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    const refresh = () => {
      api.get("/atelier/summary").then((r) => setReady(r.data.ready || 0)).catch(() => {});
      api.get("/images/quota").then((r) => setQuota(r.data)).catch(() => {});
    };
    refresh();
    const id = setInterval(refresh, 30000);
    window.addEventListener("atelier-refresh", refresh);
    return () => { clearInterval(id); window.removeEventListener("atelier-refresh", refresh); };
  }, []);

  const tabs = [
    { key: "atelier", label: "Atelier", icon: Layers3, hint: "Déposez et partez" },
    { key: "rapide", label: "Mode rapide", icon: Zap, hint: "Rédaction immédiate" },
  ];

  return (
    <div className="p-8 lg:p-12">
      <div className="mb-2 flex items-center gap-2 text-coral-dark">
        <Sparkles className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em]">Création</span>
      </div>
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
        <h1 className="font-serif text-4xl text-slate-900">Créer un post</h1>
        {quota && Number.isFinite(quota.limit) && (
          quota.remaining > 0 ? (
            <span data-testid="quota-visuels"
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${
                quota.remaining <= 2
                  ? "bg-amber-50 border-amber-200 text-amber-800"
                  : "bg-slate-50 border-slate-200 text-slate-500"
              }`}>
              <ImageIcon className="h-3.5 w-3.5" />
              {quota.remaining === 1
                ? "Il vous reste 1 visuel ce mois-ci"
                : `Il vous reste ${quota.remaining} visuels ce mois-ci`}
            </span>
          ) : (
            <span data-testid="quota-visuels-epuise"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5" />
              Visuels épuisés pour ce mois : vos textes restent disponibles
            </span>
          )
        )}
      </div>

      <RecoveredVisuals />

      <div className="flex flex-wrap gap-2 mb-8 border-b border-slate-200">
        {tabs.map((t) => (
          <button key={t.key} data-testid={`creer-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`group relative flex items-center gap-2.5 px-5 py-3 text-sm font-semibold transition-colors -mb-px border-b-2 ${
              tab === t.key
                ? "border-coral text-coral-dark"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>
            <t.icon className="h-4 w-4" />
            <span>{t.label}</span>
            <span className="hidden sm:inline text-xs font-normal text-slate-400">· {t.hint}</span>
            {t.key === "atelier" && ready > 0 && (
              <span data-testid="creer-tab-badge"
                className="ml-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-coral-strong rounded-full">
                {ready}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "atelier" ? <Atelier embedded /> : <Generator embedded />}
    </div>
  );
}
